import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_LEAD_WORKING_DAYS,
  addWorkingDays,
} from '@/domain';
import { useJobOrderRepository, usePrintRepository, useQrEncoder } from '../container';
import { keys } from '../query-keys';
import type { CreateJobOrderCommand } from '../ports/JobOrderRepository';

export function useCatalog() {
  const repo = useJobOrderRepository();
  return useQuery({
    queryKey: keys.catalog,
    queryFn: () => repo.catalog(),
    // The catalogue is a price list, not live data.
    staleTime: Infinity,
  });
}

export function useCreateJobOrder() {
  const repo = useJobOrderRepository();
  const queryClient = useQueryClient();
  /*
   * One Idempotency-Key per case being entered, kept until that case is made.
   * A Create pressed again after the answer was lost (a dropped connection at
   * the desk) sends the same key, and the server answers with the case it
   * already made instead of making a second. Only a success is remembered on
   * the server, so a corrected form after a refusal is entered afresh.
   */
  const requestId = useRef<string | null>(null);

  return useMutation({
    mutationFn: (command: CreateJobOrderCommand) => {
      requestId.current ??= crypto.randomUUID();
      return repo.create(command, requestId.current);
    },
    onSuccess: () => {
      requestId.current = null;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.all });
      void queryClient.invalidateQueries({ queryKey: keys.orders.all });
      void queryClient.invalidateQueries({ queryKey: keys.board() });
      // A case booked in under a new name makes a doctor's or a patient's
      // record, and every case counts on its doctor's and its patient's. The
      // request of 6 October 2026 (second), items 4 and 5.
      void queryClient.invalidateQueries({ queryKey: keys.doctors.all });
      void queryClient.invalidateQueries({ queryKey: keys.patients.all });
    },
  });
}

export function usePrintPayload(kitId: string | undefined) {
  const repo = usePrintRepository();
  return useQuery({
    queryKey: keys.print(kitId ?? ''),
    queryFn: () => repo.printPayload(kitId as string),
    enabled: kitId !== undefined,
    // Printing must work from a cold cache but the payload is not live data.
    staleTime: 5 * 60_000,
  });
}

export function useQrMatrix(value: string | undefined) {
  const encoder = useQrEncoder();
  return useQuery({
    queryKey: ['qr', value ?? ''],
    queryFn: () => encoder.encode(value as string),
    enabled: value !== undefined && value !== '',
    staleTime: Infinity,
  });
}

/**
 * The live preview inside the intake form while the user chooses a start date.
 *
 * The SERVER is authoritative for the delivery date, section 4.4. This exists
 * so the field is not blank while the form is being filled in, and the value it
 * shows is replaced by whatever the server returns on create.
 */
export function previewDeliveryDate(startDate: string): string {
  const [year, month, day] = startDate.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) return '';

  const start = new Date(year, month - 1, day);
  if (Number.isNaN(start.getTime())) return '';

  const delivery = addWorkingDays(start, DEFAULT_LEAD_WORKING_DAYS);
  const paddedMonth = String(delivery.getMonth() + 1).padStart(2, '0');
  const paddedDay = String(delivery.getDate()).padStart(2, '0');
  return `${delivery.getFullYear()}-${paddedMonth}-${paddedDay}`;
}

/**
 * The units a case covers, given what the person filling the form has done.
 *
 * The chart and the count are two ways of saying the same thing and reception
 * may use either or both. Marking teeth sets the count; typing a count without
 * marking teeth is equally valid, because reception often takes a case over
 * the phone knowing there are four crowns before knowing which four.
 *
 * Where the two disagree the LARGER wins, and that is the whole rule. A case
 * with six units of which four are identified so far is a real and common
 * state, and the old behaviour, which locked the field to the chart, made it
 * impossible to record: you could say which teeth or how many, never both.
 * A count below the number of marked teeth is not a smaller case, it is an
 * unfinished edit, and the form says so rather than silently discarding a
 * tooth somebody deliberately marked.
 */
export function unitsFor(teeth: string[], typed: number): number {
  return Math.max(teeth.length, Math.max(0, typed));
}

/**
 * True when a typed count is below the teeth already marked, which the form
 * shows as a note rather than an error: nothing is lost, the count has simply
 * been raised back to what the chart says.
 */
export function countIsBelowChart(teeth: string[], typed: number): boolean {
  return typed > 0 && teeth.length > 0 && typed < teeth.length;
}

/**
 * Units the case covers that have no tooth marked against them yet. Zero when
 * the chart is empty, because then nothing has been identified and there is no
 * gap to report.
 */
export function unidentifiedUnits(teeth: string[], typed: number): number {
  if (teeth.length === 0) return 0;
  return Math.max(0, unitsFor(teeth, typed) - teeth.length);
}
